import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import api, { apiErr } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { KeyRound, Plus, Copy, Ban, Code2, Check } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

function CopyBtn({ text, testid }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" data-testid={testid} onClick={() => { navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); }}
      className="p-1.5 rounded-lg text-slate-500 hover:text-[#0A1F44] hover:bg-slate-100">
      {ok ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

export default function Integraciones() {
  const { setMobileOpen } = useOutletContext();
  const [keys, setKeys] = useState([]);
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => { api.get("/api-keys").then(({ data }) => setKeys(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const crear = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const { data } = await api.post("/api-keys", { name }); setCreated(data); setName(""); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
    finally { setLoading(false); }
  };
  const revocar = async (k) => {
    try { await api.delete(`/api-keys/${k.id}`); toast.success("API key revocada"); load(); } catch (e2) { toast.error(apiErr(e2)); }
  };

  const curlExample = `curl -X POST ${BASE}/api/integraciones/envios \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: TU_API_KEY" \\
  -d '{
    "destinatario": "María González",
    "whatsapp": "0981123456",
    "ciudad": "Ciudad del Este",
    "valor_producto": 250000,
    "costo_envio": 30000,
    "modalidad": "cod",
    "referencia": "PEDIDO-1042"
  }'`;

  return (
    <>
      <Header title="API / Integraciones" subtitle="Conectá tu tienda online con Pya'elino" onMenu={() => setMobileOpen(true)}
        action={<button onClick={() => { setShow(true); setCreated(null); }} data-testid="new-apikey-btn" className="flex items-center gap-2 text-sm font-semibold bg-[#0A1F44] hover:bg-[#132D5C] text-white rounded-lg px-3.5 py-2"><Plus className="h-4 w-4" /> Nueva API key</button>} />
      <main className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Keys */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 font-semibold text-[#0A1F44]"><KeyRound className="h-4 w-4 text-[#F5A623]" /> API Keys activas</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[#64748B] text-xs uppercase"><tr><th className="text-left font-semibold px-4 py-3">Nombre</th><th className="text-left font-semibold px-4 py-3">Prefijo</th><th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Creada</th><th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Último uso</th><th className="text-right font-semibold px-4 py-3">Acciones</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {keys.map((k) => (
                  <tr key={k.id} className="hover:bg-slate-50/60" data-testid={`apikey-row-${k.id}`}>
                    <td className="px-4 py-3 font-semibold text-[#0A1F44]">{k.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{k.prefix}…</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-xs text-[#64748B]">{k.created_at ? new Date(k.created_at).toLocaleDateString("es-PY") : "—"}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs text-[#64748B]">{k.last_used_at ? new Date(k.last_used_at).toLocaleString("es-PY") : "Nunca"}</td>
                    <td className="px-4 py-3 text-right">
                      {k.activo
                        ? <button onClick={() => revocar(k)} data-testid={`revoke-apikey-${k.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 border border-rose-300 hover:bg-rose-50 rounded-lg px-3 py-1.5"><Ban className="h-3.5 w-3.5" /> Revocar</button>
                        : <span className="text-xs text-slate-400 font-medium">Revocada</span>}
                    </td>
                  </tr>
                ))}
                {keys.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#64748B]">No hay API keys. Creá una para conectar tu tienda.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Docs */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-5 sm:p-6 space-y-4">
          <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center"><Code2 className="h-5 w-5 text-[#3B82F6]" /></div><div className="font-bold text-[#0A1F44] font-display">Documentación para tiendas online</div></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="text-xs font-semibold text-[#64748B] uppercase mb-1">Base URL</div>
              <div className="flex items-center justify-between gap-2"><code className="font-mono text-xs text-[#0A1F44] break-all">{BASE}/api</code><CopyBtn text={`${BASE}/api`} testid="copy-base-url" /></div>
              <div className="text-xs font-semibold text-[#64748B] uppercase mt-3 mb-1">Autenticación</div>
              <code className="font-mono text-xs text-[#0A1F44]">Header: X-API-Key: pya_...</code>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="text-xs font-semibold text-[#64748B] uppercase mb-1">Endpoints</div>
              <ul className="font-mono text-xs text-[#0A1F44] space-y-1.5">
                <li><span className="text-emerald-600 font-bold">POST</span> /integraciones/envios</li>
                <li><span className="text-blue-600 font-bold">GET</span> /integraciones/envios/{"{code}"}</li>
                <li><span className="text-blue-600 font-bold">GET</span> /track/{"{code}"} <span className="text-slate-400">(público)</span></li>
              </ul>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-[#64748B] uppercase mb-1">Ejemplo: crear un envío desde tu tienda</div>
            <div className="relative">
              <pre className="rounded-lg bg-[#0A1F44] text-slate-200 text-xs p-4 overflow-x-auto font-mono">{curlExample}</pre>
              <div className="absolute top-2 right-2"><CopyBtn text={curlExample} testid="copy-curl-example" /></div>
            </div>
          </div>
          <p className="text-sm text-[#64748B]">La ciudad destino se rutea automáticamente a la sucursal correspondiente (ASU, COV, CDE, ENC, CAA). La respuesta incluye <code className="font-mono text-xs bg-slate-100 px-1 rounded">tracking_url</code> para compartir con tu cliente.</p>
        </div>
      </main>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">{created ? "API key creada" : "Nueva API key"}</DialogTitle><DialogDescription>{created ? "Guardala ahora: no se vuelve a mostrar." : "Asignale un nombre identificatorio (ej. Mi tienda Shopify)."}</DialogDescription></DialogHeader>
          {!created ? (
            <form onSubmit={crear} className="space-y-3">
              <input className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100" placeholder="Nombre (ej. Tienda WooCommerce)" value={name} onChange={(e) => setName(e.target.value)} required data-testid="apikey-name-input" />
              <button disabled={loading} data-testid="apikey-submit-btn" className="w-full rounded-lg bg-[#0A1F44] text-white font-semibold py-2.5 text-sm disabled:opacity-60">{loading ? "Generando..." : "Generar API key"}</button>
            </form>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">Esta key se muestra una sola vez. Copiala y guardala en un lugar seguro.</div>
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
                <code className="font-mono text-xs text-[#0A1F44] break-all flex-1" data-testid="apikey-value">{created.key}</code>
                <CopyBtn text={created.key} testid="copy-apikey-btn" />
              </div>
              <button onClick={() => setShow(false)} className="w-full rounded-lg border border-slate-300 py-2 text-sm font-medium">Listo</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
